import { ComputedDatum, ResponsivePie } from "@nivo/pie";

import { EmptyChartCardComponent } from "@zenith/features/Analytics";
import {
  PaymentChargedDTO,
  TotalChargedDTO,
} from "@zenith/services/transactions";
import { getSuffixedNumber, formatCurrency } from "@zenith/utils";
import {
  getPaymentMethodColor,
  isReservedPaymentMethodColor,
} from "@zenith/utils/Helpers";
import { DEFAULT_CURRENCY_SYMBOL } from "@zenith/data/constants";
import { memo } from "react";

interface PieChartComponentProps {
  data: PaymentChargedDTO[];
  isQuantity: boolean;
  totalValueData: TotalChargedDTO;
}

interface PieDataProps {
  id: string;
  label: string;
  value: number;
  formattedValue?: string;
  color: string;
  index: number;
  indexValue: string | number;
}

const PERCENTAGE_TO_RENDER_VALUE = 0.05;

// Custom tooltip component
const CustomPieTooltip = ({
  datum,
  isQuantity,
}: {
  datum: any;
  isQuantity: boolean;
}) => (
  <div className="rounded-lg bg-neutral-800 px-3 py-2 text-white shadow-lg">
    <p className="text-sm font-medium">
      {datum.id}:{" "}
      {isQuantity
        ? datum.value
        : `${DEFAULT_CURRENCY_SYMBOL} ${formatCurrency(datum.value, false)}`}
    </p>
  </div>
);

/**
 * A simple pie chart component.
 * It renders for amount values (currency) or quantity values,
 * defined by the 'isQuantity' prop.
 * It can also sort the chart alphabetically.
 * @param {PieChartComponentProps} props
 * @returns {JSX.Element}
 */
const PieChartComponent = memo(function PieChartComponent(
  props: PieChartComponentProps
) {
  const { data, isQuantity, totalValueData } = props;
  const pieData: PieDataProps[] = [];

  const sorted = data?.length ? [...data] : [];

  if (sorted.length > 1) {
    sorted.sort((a, b) => {
      const nameA = (a.paymentMethod as PaymentChargedDTO.paymentMethod)
          .mapPaymentMethod()
          .toUpperCase(),
        nameB = (b.paymentMethod as PaymentChargedDTO.paymentMethod)
          .mapPaymentMethod()
          .toUpperCase();

      if (nameA < nameB) {
        return -1;
      }

      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });
  }

  // Inward and POS get fixed colors below; everything else is indexed among
  // just the non-reserved methods, so the shared palette still runs in a
  // stable order instead of skipping the slots Inward/POS would have used.
  let fallbackIdx = 0;
  sorted?.forEach((item, idx) => {
    const label = item.paymentMethod?.mapPaymentMethod() as string;
    const color = getPaymentMethodColor(label, fallbackIdx);
    if (!isReservedPaymentMethodColor(label)) fallbackIdx++;

    if (isQuantity && (item.quantity as number) > 0) {
      pieData.push({
        id: label,
        label,
        value: item.quantity as number,
        color,
        index: idx,
        indexValue: idx,
      });
    } else if ((item.amount as number) > 0) {
      pieData.push({
        id: label,
        label,
        value: item.amount as number,
        color,
        index: idx,
        indexValue: idx,
      });
    }
  });

  function handleArcLabelValue(datum: ComputedDatum<PieDataProps>): string {
    const { value, formattedValue } = datum;
    const minAmount =
      value < (totalValueData.amount as number) * PERCENTAGE_TO_RENDER_VALUE;

    if (isQuantity && value > 0) {
      return formattedValue;
    }

    if (value > 0 && !minAmount) {
      return formattedValue;
    }

    return "";
  }

  return (
    <div className="flex rounded-lg border border-zinc-300 bg-white p-8">
      {!pieData.length ? (
        <EmptyChartCardComponent />
      ) : (
        <div
          className="flex h-full w-full"
          style={{ height: "24rem", maxWidth: 500 }}
        >
          <ResponsivePie
            data={pieData}
            margin={{ right: 150, left: 10 }}
            innerRadius={0.5}
            cornerRadius={0}
            colors={{ datum: "data.color" }}
            theme={{
              fontFamily: "Outfit",
              tooltip: {
                container: {
                  background: "#373737",
                  color: "white",
                  borderRadius: "8px",
                  fontSize: "12px",
                  padding: "8px 12px",
                },
              },
            }}
            activeOuterRadiusOffset={8}
            borderWidth={1}
            borderColor={{
              from: "color",
              modifiers: [["darker", 0.1]],
            }}
            valueFormat={(value) =>
              isQuantity ? value.toString() : getSuffixedNumber(value)
            }
            enableArcLabels={true}
            arcLabelsTextColor={"white"}
            arcLabelsSkipAngle={0}
            arcLabel={(a) => handleArcLabelValue(a)}
            enableArcLinkLabels={false}
            arcLinkLabelsSkipAngle={2}
            arcLinkLabelsColor={{
              from: "color",
            }}
            arcLinkLabelsThickness={1}
            arcLinkLabel={(d) => (d.value > 0 ? `${d.id}: ${d.value}` : "")}
            tooltip={({ datum }) => (
              <CustomPieTooltip datum={datum} isQuantity={isQuantity} />
            )}
            legends={[
              {
                anchor: "right",
                direction: "column",
                justify: false,
                translateX: 150,
                itemsSpacing: 20,
                itemWidth: 100,
                itemHeight: 18,
                itemTextColor: "#1A1C1F",
                itemDirection: "left-to-right",
                itemOpacity: 1,
                symbolSize: 18,
                symbolShape: "circle",
              },
            ]}
          />
        </div>
      )}
    </div>
  );
});

export default PieChartComponent;
